/**
 * Each AI bulb remembers its own tier (#82), independent of the others and of the account's
 * capture tier. Before this, the Add card's bulb, the Inbox retry bulb and the Create-task
 * form's bulb all read and wrote one account-wide setting that also decided whether the
 * background worker ran — so choosing "Off" to keep an item for testing also switched off the
 * bulb you wanted to test it with.
 *
 * A pick is remembered per *surface* (one per kind of bulb, not per row) in this device's
 * localStorage, the way the layout and Display settings are. Until a bulb has been picked on, it
 * shows the account's capture tier from Settings, so nothing visibly changes for anyone who
 * never touches one. That account tier is now only the default for what arrives with no bulb at
 * all — the watch.
 */

import { useCallback, useSyncExternalStore } from "react";
import type { CaptureTier } from "./api";
import { useCaptureTier } from "./captureTierContext";

export type BulbSurface =
  | "add"
  | "inbox-retry"
  | "inbox-refine"
  | "note-form"
  | "task-form"
  | "project-form"
  | "context-form";

const PREFIX = "efss.bulbTier.";
const TIERS: readonly string[] = ["off", "dim", "on", "bright"];
const listeners = new Set<() => void>();

function read(surface: BulbSurface): CaptureTier | null {
  try {
    const stored = window.localStorage.getItem(PREFIX + surface);
    return stored && TIERS.includes(stored) ? (stored as CaptureTier) : null;
  } catch {
    return null; // storage blocked: the bulb just follows the account tier
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  window.addEventListener("storage", listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", listener);
  };
}

export function useBulbTier(surface: BulbSurface): {
  /** The tier this bulb will run at; `null` only while the account tier is still loading. */
  tier: CaptureTier | null;
  setTier: (next: CaptureTier) => void;
} {
  const stored = useSyncExternalStore(
    subscribe,
    () => read(surface),
    () => null,
  );
  const { tier: account } = useCaptureTier();
  const setTier = useCallback(
    (next: CaptureTier) => {
      try {
        window.localStorage.setItem(PREFIX + surface, next);
      } catch {
        // Not remembered, but still announced below so this session's bulbs agree.
      }
      for (const listener of listeners) listener();
    },
    [surface],
  );
  return { tier: stored ?? account, setTier };
}
